import {
  Profile,
  AuthMethod,
  Credentials,
  AuthResult,
  GameResult,
  UserPreferences,
  GameStatistics,
  AIDifficulty,
  GameMode
} from '../types';
import { ProfileManager as IProfileManager } from '../interfaces/ProfileManager';
import { LocalStorageManager } from './LocalStorageManager';
import { 
  isValidCredentials, 
  isValidEmail, 
  isValidPassword, 
  generateId 
} from '../utils/validation';

export class ProfileManager implements IProfileManager {
  private static readonly FREE_GAME_LIMIT = 3;
  private localStorageManager: LocalStorageManager;
  private currentProfile: Profile | null = null;

  constructor() {
    this.localStorageManager = new LocalStorageManager();
    this.loadCurrentProfile();
  }

  /**
   * Creates a new user profile
   */
  async createProfile(authMethod: AuthMethod, credentials: Credentials): Promise<Profile> {
    // For custom accounts, validate email and password requirements first
    if (authMethod === AuthMethod.CUSTOM) {
      if (!credentials.email || !isValidEmail(credentials.email)) {
        throw new Error('Valid email address is required');
      }
      
      if (!credentials.password || !isValidPassword(credentials.password)) {
        throw new Error('Password must be at least 8 characters with uppercase, lowercase, number, and special character');
      }
    }

    // Validate credentials based on auth method
    if (!isValidCredentials(credentials, authMethod)) {
      throw new Error('Invalid credentials for the specified authentication method');
    }

    // Create new profile
    const profile: Profile = {
      id: generateId(),
      name: this.extractNameFromCredentials(credentials, authMethod),
      email: credentials.email,
      authMethod,
      preferences: this.getDefaultPreferences(),
      statistics: this.getDefaultStatistics(),
      createdAt: new Date()
    };

    // Save profile and reset guest game count
    this.localStorageManager.saveProfile(profile);
    this.localStorageManager.resetGuestGameCount();
    this.currentProfile = profile;

    return profile;
  }

  /**
   * Authenticates a user
   */
  async authenticateUser(authMethod: AuthMethod, credentials: Credentials): Promise<AuthResult> {
    try {
      // Validate credentials
      if (!isValidCredentials(credentials, authMethod)) {
        return {
          success: false,
          error: 'Invalid credentials'
        };
      }

      // For guest mode, no authentication needed
      if (authMethod === AuthMethod.GUEST) {
        return {
          success: true,
          profile: undefined // No profile for guest mode
        };
      }

      // For custom authentication, simulate validation
      if (authMethod === AuthMethod.CUSTOM) {
        // In a real implementation, this would validate against a backend
        // For now, we'll check if a profile exists with this email
        const existingProfile = this.localStorageManager.getProfile();
        
        if (existingProfile && existingProfile.email === credentials.email) {
          this.currentProfile = existingProfile;
          return {
            success: true,
            profile: existingProfile,
            token: this.generateAuthToken()
          };
        } else {
          return {
            success: false,
            error: 'Invalid email or password'
          };
        }
      }

      // For third-party authentication (Google, Facebook, etc.)
      // In a real implementation, this would validate the token with the provider
      if (credentials.token) {
        // Simulate successful third-party authentication
        const existingProfile = this.localStorageManager.getProfile();
        
        if (existingProfile && existingProfile.authMethod === authMethod) {
          this.currentProfile = existingProfile;
          return {
            success: true,
            profile: existingProfile,
            token: credentials.token
          };
        } else {
          // Create new profile for third-party auth if none exists
          const newProfile = await this.createProfile(authMethod, credentials);
          return {
            success: true,
            profile: newProfile,
            token: credentials.token
          };
        }
      }

      return {
        success: false,
        error: 'Authentication failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      };
    }
  }

  /**
   * Synchronizes game data across devices
   */
  async syncGameData(profile: Profile): Promise<void> {
    try {
      // Update local profile
      this.localStorageManager.saveProfile(profile);
      this.currentProfile = profile;
      
      // Sync with AWS backend if online
      if (navigator.onLine) {
        // This would integrate with DataSynchronizationService
        // For now, we'll use the existing API
        const success = await this.syncWithBackend(profile);
        if (!success) {
          console.warn('Backend sync failed, data saved locally');
        }
      }
    } catch (error) {
      throw new Error('Failed to synchronize game data');
    }
  }

  /**
   * Sync profile data with backend
   */
  private async syncWithBackend(profile: Profile): Promise<boolean> {
    try {
      // This would be implemented with actual AWS API calls
      // For now, we'll simulate the sync
      console.log('Syncing profile data with backend for user:', profile.id);
      return true;
    } catch (error) {
      console.error('Backend sync failed:', error);
      return false;
    }
  }

  /**
   * Updates user statistics with a new game result
   */
  async updateStatistics(gameResult: GameResult): Promise<void> {
    try {
      // Update statistics in local storage
      const updatedStats = this.localStorageManager.recordGameResult(gameResult);
      
      // Update current profile if exists
      if (this.currentProfile) {
        this.currentProfile.statistics = updatedStats;
        this.localStorageManager.saveProfile(this.currentProfile);
      }
    } catch (error) {
      throw new Error('Failed to update statistics');
    }
  }

  /**
   * Gets the current user profile
   */
  getCurrentProfile(): Profile | null {
    return this.currentProfile;
  }

  /**
   * Updates user preferences
   */
  async updatePreferences(preferences: Partial<UserPreferences>): Promise<void> {
    try {
      const currentPrefs = this.localStorageManager.getPreferences();
      const updatedPrefs = { ...currentPrefs, ...preferences };
      
      this.localStorageManager.savePreferences(updatedPrefs);
      
      // Update current profile if exists
      if (this.currentProfile) {
        this.currentProfile.preferences = updatedPrefs;
        this.localStorageManager.saveProfile(this.currentProfile);
      }
    } catch (error) {
      throw new Error('Failed to update preferences');
    }
  }

  /**
   * Logs out the current user
   */
  async logout(): Promise<void> {
    try {
      // Clear tokens first
      const { AuthTokenManager } = await import('./AuthTokenManager');
      const tokenManager = AuthTokenManager.getInstance();
      tokenManager.clearTokens();
      
      // Clear current profile
      this.currentProfile = null;
      
      console.log('🔓 User logged out successfully');
    } catch (error) {
      throw new Error('Failed to logout');
    }
  }

  /**
   * Checks if user has reached the free game limit
   */
  hasReachedFreeGameLimit(): boolean {
    // If user has a profile, they don't have game limits
    if (this.currentProfile) {
      return false;
    }
    
    // Check guest game count
    const guestGameCount = this.localStorageManager.getGuestGameCount();
    return guestGameCount >= ProfileManager.FREE_GAME_LIMIT;
  }

  /**
   * Increments the guest game counter
   */
  incrementGuestGameCount(): void {
    // Only increment if user doesn't have a profile
    if (!this.currentProfile) {
      this.localStorageManager.incrementGuestGameCount();
    }
  }

  /**
   * Gets the current guest game count
   */
  getGuestGameCount(): number {
    return this.localStorageManager.getGuestGameCount();
  }

  /**
   * Prompts user to create profile after reaching game limit
   */
  shouldPromptForProfile(): boolean {
    return this.hasReachedFreeGameLimit() && !this.currentProfile;
  }

  /**
   * Load current profile from storage on initialization
   */
  private loadCurrentProfile(): void {
    try {
      this.currentProfile = this.localStorageManager.getProfile();
    } catch (error) {
      console.warn('Failed to load current profile:', error);
      this.currentProfile = null;
    }
  }

  /**
   * Extract name from credentials based on auth method
   */
  private extractNameFromCredentials(credentials: Credentials, authMethod: AuthMethod): string {
    // If credentials have an email, use the part before @ as the name
    if (credentials.email) {
      return credentials.email.split('@')[0];
    }

    switch (authMethod) {
      case AuthMethod.CUSTOM:
        return credentials.email ? credentials.email.split('@')[0] : 'User';
      
      case AuthMethod.GOOGLE:
        return credentials.email ? credentials.email.split('@')[0] : 'Google User';
      
      case AuthMethod.FACEBOOK:
        return credentials.email ? credentials.email.split('@')[0] : 'Facebook User';
      
      case AuthMethod.APPLE:
        return credentials.email ? credentials.email.split('@')[0] : 'Apple User';
      
      case AuthMethod.YAHOO:
        return credentials.email ? credentials.email.split('@')[0] : 'Yahoo User';
      
      default:
        return 'User';
    }
  }

  /**
   * Generate authentication token (simplified for demo)
   */
  private generateAuthToken(): string {
    return `token_${generateId()}_${Date.now()}`;
  }

  /**
   * Get default user preferences
   */
  private getDefaultPreferences(): UserPreferences {
    return {
      theme: 'default',
      soundEnabled: true,
      defaultGameMode: GameMode.SINGLE_PLAYER,
      notifications: {
        turnBased: true,
        gameInvites: true,
        gameResults: true
      }
    };
  }

  /**
   * Get default game statistics
   */
  private getDefaultStatistics(): GameStatistics {
    return {
      singlePlayer: {
        [AIDifficulty.EASY]: { wins: 0, losses: 0, draws: 0 },
        [AIDifficulty.MEDIUM]: { wins: 0, losses: 0, draws: 0 },
        [AIDifficulty.HARD]: { wins: 0, losses: 0, draws: 0 }
      },
      multiplayer: { wins: 0, losses: 0, draws: 0 },
      totalGames: 0,
      winRate: 0
    };
  }
}